import React, { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Sparkles, Trophy, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * A two-layer early-adopter prompt:
 *  1. WELCOME — shown once right after a free user logs in for the first time.
 *     Message: "You deserve more — free upgrade to Starter Pack unlocks at 5 invoices."
 *  2. CELEBRATION — fires automatically the moment invoice #5 is processed,
 *     claims the server-side bonus, and shows the self-starter celebration.
 *
 * The component polls /api/early-adopter/status, so it's safe to mount once
 * on the dashboard root. No props needed.
 */
export default function EarlyAdopterPrompt({ onClaimed }) {
  const [status, setStatus] = useState(null);
  const [stage, setStage] = useState(null); // 'welcome' | 'celebrate' | null
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = async () => {
    try {
      const res = await fetch(`${API}/early-adopter/status`, { credentials: "include" });
      if (!res.ok) return null;
      const d = await res.json();
      setStatus(d);
      return d;
    } catch {
      return null;
    }
  };

  // Initial load + decide whether to show welcome
  // On mount: decide whether to show the welcome banner OR fire the
  // celebration directly. The celebration path covers users whose plan was
  // already auto-flipped to "starter" by the inline upload-time claim
  // (server-side) before this component had a chance to poll.
  useEffect(() => {
    (async () => {
      const d = await refresh();
      if (!d) return;
      // If the user was just auto-claimed by the upload endpoint, the API
      // returns claimed=true + slotNumber + plan="starter". Surface the
      // celebration immediately — but only once per session.
      if (d.claimed && d.slotNumber && !sessionStorage.getItem("ea_celebrated")) {
        sessionStorage.setItem("ea_celebrated", "1");
        setResult({
          alreadyClaimed: true,
          slotNumber: d.slotNumber,
          cap: d.cap,
          plan: "starter",
          planAccessUntil: d.planAccessUntil || new Date(Date.now() + d.creditDays * 86400000).toISOString(),
          creditDays: d.creditDays,
        });
        setStage("celebrate");
        try {
          confetti({
            particleCount: 160,
            spread: 110,
            origin: { y: 0.55 },
            colors: ["#FF6B00", "#1D9E75", "#F0EDE8", "#FFB066"],
          });
        } catch {
          /* ignore */
        }
        return;
      }
      if (d.capReached || d.claimed || d.plan !== "free") return;
      // Show the welcome modal only once
      if (!d.welcomeShown && d.eligible) setStage("welcome");
    })();
  }, []);

  // Poll every 5s after user has seen welcome — we want to auto-trigger
  // celebration as soon as the server acknowledges the 5th invoice.
  useEffect(() => {
    if (!status || status.claimed || status.capReached) return;
    if (status.plan !== "free") return;
    const id = setInterval(async () => {
      const d = await refresh();
      if (!d) return;
      if (d.claimed) {
        clearInterval(id);
        return;
      }
      if (d.invoiceCount >= d.threshold && d.eligible && !d.claimed) {
        clearInterval(id);
        await doClaim();
      }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.claimed, status?.invoiceCount, status?.capReached]);

  const acknowledgeWelcome = async () => {
    setStage(null);
    try {
      await fetch(`${API}/early-adopter/welcome-ack`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* non-fatal */
    }
  };

  const doClaim = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/early-adopter/claim`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("claim failed");
      const d = await res.json();
      setResult(d);
      setStage("celebrate");
      try {
        confetti({
          particleCount: 160,
          spread: 110,
          origin: { y: 0.55 },
          colors: ["#FF6B00", "#1D9E75", "#F0EDE8", "#FFB066"],
        });
      } catch {
        /* ignore */
      }
      if (onClaimed) onClaimed(d);
    } catch {
      /* silent — user can retry by re-uploading */
    } finally {
      setBusy(false);
    }
  };

  if (!stage) return null;

  if (stage === "welcome" && status) {
    const remaining = Math.max(0, status.remainingForBonus || 0);
    const invoices = status.invoiceCount || 0;
    const threshold = status.threshold || 5;
    return (
      <ModalShell onClose={acknowledgeWelcome} testId="early-adopter-welcome">
        <div className="space-y-4 text-center">
          <div className="w-14 h-14 mx-auto bg-[#FF6B00]/15 border border-[#FF6B00]/40 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-[#FF6B00]" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
            — You deserve more
          </div>
          <h2
            className="font-display uppercase text-3xl md:text-4xl leading-[1.05] text-[#F0EDE8]"
            style={{ fontFamily: "'Pirata One', serif" }}
          >
            Free upgrade to{" "}
            <span className="text-[#FF6B00]">Starter Pack</span>
          </h2>
          <p className="text-sm md:text-base text-[#D0CFC9] leading-relaxed max-w-md mx-auto">
            You're one of our first 250 launch users. Upload{" "}
            <span className="font-bold text-[#F0EDE8]">{threshold} invoices</span> to
            unlock <span className="font-bold text-[#1D9E75]">one full year of Starter benefits</span>{" "}
            — absolutely free. No card. No catch.
          </p>
          <div className="grid grid-cols-[1fr_auto] items-center gap-3 max-w-sm mx-auto bg-[#14141A] border border-[#2A2A35] px-4 py-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Your progress</div>
              <div className="font-mono-num text-2xl text-[#F0EDE8] mt-1">
                {invoices}
                <span className="text-[#666] text-lg">/{threshold}</span>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold text-right">
              {remaining === 0 ? "Ready!" : `${remaining} to go`}
            </div>
          </div>
          <button
            type="button"
            onClick={acknowledgeWelcome}
            className="btn-saffron w-full sm:w-auto"
            data-testid="early-adopter-welcome-continue"
          >
            Start uploading →
          </button>
        </div>
      </ModalShell>
    );
  }

  if (stage === "celebrate" && result) {
    return (
      <ModalShell
        onClose={() => setStage(null)}
        testId="early-adopter-celebration"
      >
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 mx-auto bg-[#1D9E75]/20 border-2 border-[#1D9E75] flex items-center justify-center">
            <Trophy className="w-8 h-8 text-[#1D9E75]" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold">
            — Self Starter, unlocked
          </div>
          <h2
            className="font-display uppercase text-3xl md:text-4xl leading-[1.05] text-[#F0EDE8]"
            style={{ fontFamily: "'Pirata One', serif" }}
          >
            You are a <span className="text-[#FF6B00]">gogetter</span>
            <br /> and an early adopter.
          </h2>
          <p className="text-sm md:text-base text-[#D0CFC9] leading-relaxed max-w-md mx-auto">
            Here's your <span className="font-bold text-[#1D9E75]">one year of Starter</span> benefits —
            completely free. You're a{" "}
            <span className="text-[#FF6B00] font-bold">Self Starter</span>.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto text-left bg-[#14141A] border border-[#2A2A35] px-4 py-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Slot</div>
              <div className="font-mono-num text-xl text-[#F0EDE8]">
                {result.slotNumber}
                <span className="text-[#666] text-sm">/{result.cap}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">Access until</div>
              <div className="font-mono-num text-sm text-[#1D9E75]">
                {new Date(result.planAccessUntil).toLocaleDateString("en-IN", {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                })}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-saffron w-full sm:w-auto"
            data-testid="early-adopter-celebrate-continue"
          >
            Explore Starter unlocks →
          </button>
        </div>
      </ModalShell>
    );
  }

  return null;
}

const ModalShell = ({ onClose, children, testId }) => (
  <div
    className="fixed inset-0 z-[1050] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className="bg-[#0A0A0F] border border-[#FF6B00]/40 max-w-lg w-full p-6 md:p-8 relative"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: "linear-gradient(90deg,#FF6B00 0%,#1D9E75 100%)" }}
      />
      {children}
    </div>
  </div>
);
